import React from 'react';
import { useApp } from '../../context/AppContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { 
  AlertTriangle, 
  ShoppingCart, 
  Zap, 
  Droplets, 
  Heart, 
  Sparkles,
  Check,
  Clock
} from 'lucide-react';

const CATEGORY_ICONS = {
  Groceries: ShoppingCart,
  Power: Zap,
  Water: Droplets,
  Health: Heart,
  Sanitation: Sparkles,
  System: AlertTriangle,
};

const AlertCard = ({ alert, compact = false }) => {
  const { acknowledgeAlert, getDependantById } = useApp();
  const dependant = getDependantById(alert.dependantId);
  const Icon = CATEGORY_ICONS[alert.category] || AlertTriangle;

  const getSeverityClass = (severity) => {
    switch (severity) {
      case 'Critical': return 'alert-critical border-l-4 border-l-red-500';
      case 'Warning': return 'alert-warning border-l-4 border-l-yellow-500';
      case 'Info': return 'alert-info border-l-4 border-l-blue-500';
      default: return 'alert-info';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'New':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            New
          </span>
        );
      case 'Acknowledged':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            <Clock className="w-3 h-3" />
            Acknowledged
          </span>
        );
      case 'Resolved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <Check className="w-3 h-3" />
            Resolved
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 48) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (compact) {
    return (
      <div 
        className={cn(
          "flex items-center gap-3 p-3 rounded-lg",
          getSeverityClass(alert.severity),
          alert.severity === 'Critical' && alert.status === 'New' && 'animate-pulse-soft'
        )}
        data-testid={`alert-${alert.id}`}
      >
        <div className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
          alert.severity === 'Critical' ? 'bg-red-200' : 
          alert.severity === 'Warning' ? 'bg-yellow-200' : 'bg-blue-200'
        )}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{alert.message}</p>
          <p className="text-xs text-muted-foreground">
            {dependant ? `${dependant.firstName} ${dependant.lastName}` : 'System'} · {formatDate(alert.createdAt)}
          </p>
        </div>
        {getStatusBadge(alert.status)}
      </div>
    );
  }

  return (
    <div 
      className={cn(
        "p-4 rounded-lg",
        getSeverityClass(alert.severity),
        alert.severity === 'Critical' && alert.status === 'New' && 'animate-pulse-soft'
      )}
      data-testid={`alert-${alert.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={cn(
          "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
          alert.severity === 'Critical' ? 'bg-red-200' : 
          alert.severity === 'Warning' ? 'bg-yellow-200' : 'bg-blue-200'
        )}>
          <Icon className="w-5 h-5" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {alert.type}
              </span>
              {dependant && (
                <span className="text-xs text-muted-foreground ml-2">
                  · {dependant.firstName} {dependant.lastName}
                </span>
              )}
            </div>
            {getStatusBadge(alert.status)}
          </div>
          
          <p className="text-sm font-medium mb-2">{alert.message}</p>
          
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {formatDate(alert.createdAt)}
            </span>
            
            {alert.status === 'New' && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => acknowledgeAlert(alert.id)}
                data-testid={`acknowledge-alert-${alert.id}`}
              >
                Acknowledge
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertCard;
